import React, { CSSProperties, ReactElement, ReactNode, useEffect, useState } from 'react';
import { ArrayHelpers, FieldArray, useField } from 'formik';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { BiLink, BiX } from 'react-icons/bi';
import { LinkContainer } from 'react-router-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { BaseEntiteStatique, BaseEntite, Activite } from '../../../../../common/src/entities';
import TooltipSimple from '../../Common/TooltipSimple';
import Images from '../../../../../common/src/entities/image.entity';

type Class<T = unknown> = new (...args: unknown[]) => T;

export type PropsDeChamp<TFormulaire, TEntite> = {
    id: keyof TFormulaire;
    titre?: ReactNode;
    children?: ReactNode;
    entite: Class<TEntite>;
    cacherTitre?: boolean;
    labelCol?: number;
    affichageElement: (
        value: TEntite | number,
        index: number,
        helpers: ArrayHelpers,
        affichageTexte: (value: TEntite | number) => ReactNode,
    ) => ReactElement;
    affichageTexte?: (value: TEntite | number) => ReactNode;
    affichageAjout: (values: TEntite[], helpers: ArrayHelpers, maxSize?: number, title?: string) => ReactElement;
    wrapperStyle?: CSSProperties;
    onRemove?: (index: number) => void;
    maxSize?: number;
    title?: string;
};

export const affichageElementDefaut = (entite: BaseEntiteStatique<BaseEntite>) =>
    function inner<TEntite extends BaseEntite>(
        value: TEntite | number,
        index: number,
        helpers: ArrayHelpers,
        affichageTexte: (value: TEntite | number) => ReactNode,
    ): ReactElement {
        const { remove } = helpers;
        return (
            <Row>
                <Col className="d-flex align-items-center">
                    {value && (
                        <LinkContainer to={`/dashboard/${entite?.key}/${(value as TEntite).id ?? (value as number)}`}>
                            <Button variant="link" size="sm" className="text-muted">
                                <BiLink size="1.5em" />
                            </Button>
                        </LinkContainer>
                    )}
                    {value ? affichageTexte((value as TEntite)?.id ? (value as TEntite) : (value as number)) : <></>}
                </Col>
                <Col xs="auto">
                    <TooltipSimple texte="Supprimer">
                        <Button
                            variant="link"
                            className="d-none d-sm-flex align-items-center text-muted px-0"
                            onClick={() => remove(index)}
                        >
                            <BiX size="1.25em" />
                        </Button>
                    </TooltipSimple>
                </Col>
            </Row>
        );
    };

export const affichageTexteDefaut = (value) => value.name;

export default function ChampMultipleDnd<TFormulaire, TEntite>(
    props: PropsDeChamp<TFormulaire, TEntite>,
): ReactElement {
    const {
        titre,
        id,
        children,
        cacherTitre,
        affichageElement,
        affichageAjout,
        affichageTexte = affichageTexteDefaut,
        onRemove,
        maxSize,
        title,
        labelCol,
    } = props;
    const [field, meta] = useField<TEntite[]>(String(id));
    const [fieldValue, setFieldValue] = useState<TEntite[]>([]);

    useEffect(() => {
        // When the form field value changes, update the local state
        setFieldValue(field.value || []);
    }, [field.value]);

    fieldValue.sort((a, b) => ((a as unknown) as Images).ordre_affichage - ((b as unknown) as Images).ordre_affichage);

    const [isGridView, setIsGridView] = useState(true);

    const toggleGridView = () => {
        setIsGridView(!isGridView);
    };

    const reorder = (list: TEntite[], startIndex: number, endIndex: number) => {
        // Clone the current state to avoid mutating it
        const updatedField = [...list];

        // Reorder the items in the cloned state
        const [removed] = updatedField.splice(startIndex, 1);
        updatedField.splice(endIndex, 0, removed);

        // Update the ordreAffichage property of each item
        updatedField.forEach((item, index) => {
            ((item as unknown) as Images).ordre_affichage = index;
        });

        return updatedField;
    };

    function handleOnDragEnd(result) {
        if (!result.destination) return;
        // fieldValue = reorder(fieldValue, result.source.index, result.destination.index);
        // Clone the current field value to avoid mutating the original array
        const updatedField = [...fieldValue];

        const reorderedItems = reorder(updatedField, result.source.index, result.destination.index);

        // Update the local state with the reordered items
        setFieldValue(reorderedItems);

        // Update the Formik field value with the reordered items
        field.onChange({ target: { name: field.name, value: reorderedItems } });
    }

    const cssCardBody: CSSProperties = {
        padding: '0px',
        minHeight: '30px',
        maxWidth: '61vw',
    };
    const cssContenantImages: React.CSSProperties = {
        display: 'flex',
    };
    const cssContenantImagesGrid: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
    };

    const scrollContainerStyle: CSSProperties = {
        maxWidth: '100%',
        overflowX: 'auto',
        display: 'flex',
        flexDirection: 'row',
        paddingRight: '50px',
    };

    const outerContainerStyle: CSSProperties = {
        position: 'relative',
        overflow: 'hidden',
        maxWidth: '100%',
    };

    const fadeGradientStyle: CSSProperties = {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: '200px',
        backgroundImage: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
        pointerEvents: 'none',
    };

    return (
        <FieldArray name={field.name}>
            {function ChampMultipleInterne(arrayHelpers) {
                const helpers = {
                    ...arrayHelpers,
                    remove: function <T>(entite: number) {
                        if (onRemove) onRemove(entite);
                        return arrayHelpers.remove<T>(entite);
                    },
                };
                return (
                    <Form.Group controlId={id as string}>
                        <Row style={{ flexWrap: 'nowrap' }}>
                            <Col xs={labelCol}>{cacherTitre ? null : <Form.Label>{titre}</Form.Label>}</Col>
                            <Col>
                                <DragDropContext onDragEnd={handleOnDragEnd}>
                                    <Card>
                                        <Card.Body style={cssCardBody}>
                                            <Droppable droppableId="1" direction="horizontal">
                                                {(provided) => (
                                                    <div style={outerContainerStyle}>
                                                        <div ref={provided.innerRef} style={scrollContainerStyle}>
                                                            <div
                                                                style={
                                                                    isGridView
                                                                        ? cssContenantImagesGrid
                                                                        : cssContenantImages
                                                                }
                                                            >
                                                                {fieldValue.map((value, index) => (
                                                                    <div key={index}>
                                                                        <Draggable
                                                                            draggableId={index.toString()}
                                                                            key={index.toString()}
                                                                            index={index}
                                                                            isDragDisabled={isGridView}
                                                                        >
                                                                            {(provided) => (
                                                                                <div
                                                                                    ref={provided.innerRef}
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                >
                                                                                    {affichageElement(
                                                                                        value,
                                                                                        index,
                                                                                        helpers,
                                                                                        affichageTexte,
                                                                                    )}
                                                                                    <div hidden={true}>
                                                                                        {
                                                                                            (((value as unknown) as Images).ordre_affichage = index)
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </Draggable>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div style={fadeGradientStyle}></div>
                                                            {provided.placeholder}
                                                        </div>
                                                    </div>
                                                )}
                                            </Droppable>
                                        </Card.Body>
                                    </Card>
                                </DragDropContext>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={labelCol}></Col>
                            <Col className="pt-2 d-flex align-items-center">
                                {affichageAjout(fieldValue, helpers, maxSize, title)}{' '}
                                {fieldValue.length > 1 && (
                                    <Button
                                        className="ml-2 m-0 btn-sm"
                                        variant="outline-primary"
                                        onClick={toggleGridView}
                                    >
                                        {isGridView ? 'Déplacer un élément' : 'Retour à la vue en grille'}
                                    </Button>
                                )}
                            </Col>
                        </Row>
                        <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
                        {children}
                    </Form.Group>
                );
            }}
        </FieldArray>
    );
}
